/**
 * TemplatePreview
 *
 * Detail view when a user selects a template in the gallery dialog.
 * Shows description, summary stats, connection status, and action buttons.
 * Content is centered within the dialog for a balanced layout.
 */

import { useMemo, useCallback, useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Field,
  Spinner,
} from '@coreai/fluentui-react';
import {
  ArrowLeft24Regular,
  Dismiss24Regular,
  ErrorCircle16Regular,
} from '@fluentui/react-icons';
import { useTemplates } from '@/hooks/useTemplates';
import { useTemplateApply } from '@/hooks/useTemplateApply';
import { validateWorkflowName, type NameValidationResult } from '@/utils/validation';
import { CATEGORY_DISPLAY } from '@/types/template';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import { ConnectorName } from '@/components/ConnectionList/ConnectorName';
import { extractConnectorName } from '@/utils/connections';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    ...thinScrollbarStyles,
  },
  description: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase400,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `2px ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  statText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  separator: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  connectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  connectionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} 0`,
  },
  connectionName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    flex: 1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
  },
  nameField: {
    paddingTop: tokens.spacingVerticalS,
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

// =============================================================================
// PROPS
// =============================================================================

export interface TemplatePreviewProps {
  templateId: string;
  onBack: () => void;
  /** Called when the user clicks the action button. In create-new mode, `workflowName` is passed. */
  onApply: (templateId: string, workflowName?: string) => void;
  onDismiss: () => void;
  /** When true, show a workflow-name Input and require it before the action button is enabled. */
  requireWorkflowName?: boolean;
  /** Default value for the workflow-name Input (only used when requireWorkflowName is true). */
  defaultWorkflowName?: string;
  /**
   * Existing workflow names in the app (only used when requireWorkflowName is true).
   * The create call is an upsert, so a duplicate name would silently overwrite an
   * existing workflow — block it here, like CreateWorkflowDialog does.
   */
  existingWorkflowNames?: string[] | undefined;
  /** When true, disables the action button and shows a spinner (used during workflow creation). */
  isCreating?: boolean;
  /** Override the action button label. Defaults to "Use this template". */
  submitButtonLabel?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  templateId,
  onBack,
  onApply,
  onDismiss,
  requireWorkflowName = false,
  defaultWorkflowName = '',
  existingWorkflowNames,
  isCreating = false,
  submitButtonLabel,
}) => {
  const styles = useStyles();
  const { previewConnections } = useTemplateApply();
  const [workflowName, setWorkflowName] = useState(defaultWorkflowName);

  // Reset the name field whenever we land on a fresh preview (different template
  // selected, or the default suggestion updates after workflows refetch).
  useEffect(() => {
    if (requireWorkflowName) {
      setWorkflowName(defaultWorkflowName);
    }
  }, [requireWorkflowName, defaultWorkflowName, templateId]);

  // Same rules as CreateWorkflowDialog: duplicates, reserved words, format.
  const nameValidation = useMemo<NameValidationResult>(
    () =>
      requireWorkflowName
        ? validateWorkflowName(workflowName, existingWorkflowNames ?? [])
        : { isValid: true },
    [requireWorkflowName, workflowName, existingWorkflowNames]
  );
  const trimmedName = nameValidation.normalizedName ?? workflowName.trim();
  const isNameValid = nameValidation.isValid;

  const { getTemplate } = useTemplates();

  const template = useMemo(() => getTemplate(templateId), [getTemplate, templateId]);

  const connectionResult = useMemo(
    () => (template ? previewConnections(template) : null),
    [template, previewConnections]
  );

  const matchedNameByConnectorType = useMemo(() => {
    const map = new Map<string, string>();
    if (connectionResult) {
      for (const res of connectionResult.autoMatched) {
        if (res.matchedDisplayName) {
          map.set(res.requirement.connectorType, res.matchedDisplayName);
        }
      }
    }
    return map;
  }, [connectionResult]);

  const actionCount = useMemo(
    () => Object.keys(template?.workflowDefinition.definition?.actions ?? {}).length,
    [template]
  );

  const triggerCount = useMemo(
    () => Object.keys(template?.workflowDefinition.definition?.triggers ?? {}).length,
    [template]
  );

  const handleApply = useCallback(() => {
    if (requireWorkflowName) {
      if (!isNameValid || !trimmedName) return;
      onApply(templateId, trimmedName);
    } else {
      onApply(templateId);
    }
  }, [onApply, templateId, requireWorkflowName, isNameValid, trimmedName]);

  const handleNameChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      setWorkflowName(data.value);
    },
    []
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && isNameValid && !isCreating) {
        e.preventDefault();
        handleApply();
      }
    },
    [isNameValid, isCreating, handleApply]
  );

  // Error state
  if (!template) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack} aria-label="Back to gallery" />
          <Text className={styles.headerTitle}>Template not found</Text>
          <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onDismiss} aria-label="Close" />
        </div>
        <div className={styles.errorState}>
          <ErrorCircle16Regular fontSize={40} color="inherit" />
          <Text weight="semibold">Template not found</Text>
          <Text size={200}>This template couldn't be loaded. Try again, or choose a different template.</Text>
          <Button onClick={onBack}>Back to gallery</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} role="region" aria-label={`Template details: ${template.name}`}>
      {/* Header */}
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={onBack} aria-label="Back to gallery" />
        <Text as="h2" className={styles.headerTitle} title={template.name}>
          {template.name}
        </Text>
        <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onDismiss} aria-label="Close template preview" />
      </div>

      {/* Body */}
      <div className={styles.body}>
          {/* Description */}
          <Text className={styles.description}>{template.description}</Text>

          {/* Meta: tags + stats */}
          <div className={styles.metaRow}>
            {template.category.map((c) => (
              <span key={c} className={styles.tag}>
                {CATEGORY_DISPLAY[c] ?? c}
              </span>
            ))}
            <Text className={styles.separator}>·</Text>
            <Text className={styles.statText}>
              {triggerCount} trigger{triggerCount !== 1 ? 's' : ''}, {actionCount} action{actionCount !== 1 ? 's' : ''}
            </Text>
          </div>

          {/* Connections used in this template */}
          {template.connectionRequirements.length > 0 && (
            <div className={styles.section}>
              <Text className={styles.sectionLabel}>Connections used in this template</Text>
              <div className={styles.connectionList}>
                {template.connectionRequirements.map((req) => {
                  const connectorKey = extractConnectorName(req.apiId);
                  const matchedName = matchedNameByConnectorType.get(connectorKey);
                  const typeLabel = req.connectorType === 'shared' ? 'Shared' : 'In-app';
                  return (
                    <div key={req.apiId} className={styles.connectionItem}>
                      <ConnectorName apiId={req.apiId} />
                      <Text className={styles.connectionName}>
                        ({typeLabel}){matchedName ? ` · ${matchedName}` : ''}
                      </Text>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Workflow-name input — only in create-new mode */}
          {requireWorkflowName && (
            <Field
              className={styles.nameField}
              label="Workflow name"
              required
              validationMessage={nameValidation.error ?? null}
              validationState={nameValidation.error ? 'error' : 'none'}
            >
              <Input
                value={workflowName}
                onChange={handleNameChange}
                onKeyDown={handleNameKeyDown}
                placeholder="Enter a name for your new workflow"
                disabled={isCreating}
                aria-label="New workflow name"
              />
            </Field>
          )}

          {/* Action buttons */}
          <div className={styles.actions}>
            <Button
              appearance="primary"
              onClick={handleApply}
              disabled={!isNameValid || isCreating}
              {...(isCreating ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {submitButtonLabel ?? 'Use this template'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

TemplatePreview.displayName = 'TemplatePreview';
